import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Typography, Table, Empty } from "antd";
import { Segmented } from "antd";
import { Line } from "@ant-design/charts";
import SectionLoader from "../Loaders/SectionLoader";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import moment from "moment";
import { get_language, translate } from "../../locale";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import SelfMeasureServices from "../../services/SelfMeasuerServices";
import { useParams } from "react-router-dom";
import { TableHelper } from "../../helpers/TableHelper";
import RangeForm from "./RangeForm";

const { Title } = Typography;

const BloodPressureCharts = () => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState("graph");
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(7, "days"))
  );
  const [dateTo, setDateTo] = useState(new Date());
  const data = [
    {
      year: moment().format("DD/MM/YYYY"),
      value: 0,
      type: "PAS",
    },
    {
      year: moment().format("DD/MM/YYYY"),
      value: 0,
      type: "PAD",
    },
  ];
  const dataFC = [
    {
      year: moment().format("DD/MM/YYYY"),
      value: 0,
      type: "FC",
    },
  ];
  const { username } = useParams();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);
  const [values, setValues] = useState({
    module: 3,
    subscription: 3,
    patient: username,
    type: "fc",
    max_value_alert: 150,
    max_value: 130,
    min_value: 100,
    min_value_alert: 90,
  });

  const [configGraph, setConfigGraph] = useState({
    data: data,
    height: 500,
    width: "100%",
    xField: "year",
    yField: "value",
    seriesField: "type",
    yAxis: {
      max: 180,
      min: 0,
      tickCount: 9,
      tickInterval: 20,
      title: {
        text: "Presión Arterial (mmHg)",
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 10,
    },
  });

  const [configGraphFC, setConfigGraphFC] = useState({
    data: dataFC,
    height: 500,
    width: "100%",
    xField: "year",
    yField: "value",
    seriesField: "type",
    yAxis: {
      max: 200,
      min: 0,
      tickCount: 8,
      tickInterval: 20,
      title: {
        text: "Frecuencia Cardíaca (l.p.m.)",
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 0,
    },
  });

  const [dataList, setDataList] = useState([]);

  const units = {
    // weight
    kg: "kg.",
    lb: "lb.",

    // diabetes
    mgdl: "mg/dl",

    // hypertension
    mmhg: "mmHg",
    bpm: "bpm",
  };

  const columnsList = [
    {
      title: "Fecha",
      dataIndex: "measure_date",
      key: "measure_date",
      render: (_, record) => moment(record.measure_date).format("DD/MM/YYYY"),
      sorter: { multiple: 1 },
    },
    {
      title: "Hora",
      dataIndex: "measure_time",
      key: "measure_time",
      sorter: { multiple: 2 },
    },
    {
      title: "Modo",
      dataIndex: "mode",
      key: "mode",
      render: (_, record) =>
        record.mode === "automatic" ? "Automático" : "Manual",
      sorter: { multiple: 3 },
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      render: (_, record) => {
        const s = record.answers.filter((i) => i.sistolico !== undefined).pop();
        const d = record.answers
          .filter((i) => i.diastolico !== undefined)
          .pop();
        const f = record.answers.filter((i) => i.fc !== undefined).pop();
        return (
          <>
            {`${translate("Sistólica")}: ${s.sistolico} ${units[s.unit]}`}
            <br />
            {`${translate("Diastólica")}: ${d.diastolico} ${units[d.unit]}`}
            <br />
            {`${translate("FC")}: ${f ? f.fc : ""} ${f ? units[f.unit] : ""}`}
          </>
        );
      },
      sorter: { multiple: 4 },
    },
  ];

  useEffect(() => {
    loadGraph();
  }, [JSON.stringify(tableParams)]);

  const loadGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "hypertension",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const data = res.data.controls.filter((i) => i.type === "hypertension");
        let pressure = [];
        data.forEach((i) => {
          const pas = {
            value: parseInt(
              i.answers.filter((i) => i.sistolico !== undefined).pop()
                ?.sistolico
            ),
            year: moment(`${i.measure_date} ${i.measure_time}`)
              .tz("utc")
              .local()
              .format("DD/MM/YYYY HH:mm"),
            type: "PAS",
          };
          const pad = {
            value: parseInt(
              i.answers.filter((i) => i.diastolico !== undefined).pop()
                ?.diastolico
            ),
            year: moment(`${i.measure_date} ${i.measure_time}`)
              .tz("utc")
              .local()
              .format("DD/MM/YYYY HH:mm"),
            type: "PAD",
          };
          pressure.push(pad);
          pressure.push(pas);
        });
        const fc = data.map((i) => ({
          value: parseInt(
            i.answers.filter((i) => i.fc !== undefined).pop()?.fc
          ),
          year: moment(`${i.measure_date} ${i.measure_time}`)
            .tz("utc")
            .local()
            .format("DD/MM/YYYY HH:mm"),
          type: "FC",
        }));

        setConfigGraph((v) => ({ ...v, data: pressure.reverse() }));
        setConfigGraphFC((v) => ({ ...v, data: fc.reverse() }));
        setDataList(data);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  const loadDailyPattern = async () => {
    setLoading(true);
    try {
      /* const res = await new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({
            data: [],
          });
        }, 2000);
      });
      setConfigDaily((v) => ({ ...v, data: [res.data, res.data] })); */
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const loadWeeklyPattern = async () => {
    setLoading(true);
    try {
      const res = await new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({
            data: [
              { year: "Domingo", "Presión Arterial (mmHg)": 80, HbA1c: 0 },
              { year: "Lunes", "Presión Arterial (mmHg)": 78, HbA1c: 0 },
              { year: "Martes", "Presión Arterial (mmHg)": 73, HbA1c: 0 },
              { year: "Miércoles", "Presión Arterial (mmHg)": 86, HbA1c: 0 },
              { year: "Jueves", "Presión Arterial (mmHg)": 93, HbA1c: 0 },
              { year: "Viernes", "Presión Arterial (mmHg)": 80, HbA1c: 0 },
              { year: "Sábado", "Presión Arterial (mmHg)": 78, HbA1c: 0 },
            ],
          });
        }, 2000);
      });
      setConfigGraph((v) => ({ ...v, data: [res.data, res.data] }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleChangeItem = (value) => {
    setSelectedGraph(value);
    switch (value) {
      case "graph":
        loadGraph();
        break;
      case "weekly_pattern":
        break;
      case "scattering":
        break;
      case "list":
        break;
      case "range":
        break;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };
  const onChangeDatePicker = (name, value) => {
    if (name === "dateFrom") {
      setDateFrom(value);
    } else if (name === "dateTo") {
      setDateTo(value);
    }
  };
  const handleSearch = (e) => {
    setSearching(true);
    loadGraph();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataList([]);
      setConfigGraph([]);
    }
  };

  return (
    <>
      <Form onFinish={handleSearch}>
        <Row className="mt-6 mb-14 relative z-10" gutter={[40, 10]}>
          <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
            <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
              <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                {translate("Fecha desde")}
              </div>
              <CustomDatePicker
                locale={get_language}
                selected={dateFrom}
                onChange={(e) => onChangeDatePicker("dateFrom", e)}
              />
            </div>
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
            <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
              <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                {translate("Fecha hasta")}
              </div>
              <CustomDatePicker
                locale={get_language}
                selected={dateTo}
                onChange={(e) => onChangeDatePicker("dateTo", e)}
              />
            </div>
          </Col>
          <Col
            xs={{ span: 24 }}
            md={{ span: 6 }}
            className="text-right md:text-left"
          >
            <Button
              htmlType="submit"
              type="primary"
              className="bg-custom_primary"
              icon={
                searching ? (
                  <LoadingOutlined />
                ) : (
                  <SearchOutlined style={{ position: "relative" }} />
                )
              }
            >
              {translate("Buscar")}
            </Button>
          </Col>
        </Row>
      </Form>
      <Row className="mb-14">
        <Col>
          <Segmented
            options={[
              { value: "graph", label: "Gráfico" },
              { value: "list", label: "Listado" },
              { value: "range", label: "Rangos" },
            ]}
            onChange={handleChangeItem}
          />
        </Col>
      </Row>
      <Row className="relative mb-14">
        {loading && <SectionLoader />}

        {selectedGraph === "graph" && (
          <>
            <Col span={24} className="mb-4">
              <Title level={5}>Presión Arterial</Title>
            </Col>
            <Col span={24} className="mb-8">
              <Line {...configGraph} />
            </Col>
            <Col span={24} className="mb-4">
              <Title level={5}>Frecuencia Cardíaca</Title>
            </Col>
            <Col span={24}>
              <Line {...configGraphFC} />
            </Col>
          </>
        )}
        {selectedGraph === "list" && (
          <Col span={24} className="table-record-list">
            <Table
              rowKey={(i) => `${i.measure_date} ${i.measure_time}`}
              columns={columnsList}
              dataSource={dataList}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
              }}
              pagination={{ ...tableParams.pagination, total: total }}
              onChange={handleTableChange}
            />
          </Col>
        )}
        {selectedGraph === "range" && (
          <Col span={24}>
            <Row gutter={[48, 48]}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "sistolico",
                    max_value_alert: 150,
                    max_value: 130,
                    min_value: 100,
                    min_value_alert: 90,
                  }}
                  title={translate("Rango de presión sistólica")}
                />
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "diastolico",
                    max_value_alert: 90,
                    max_value: 80,
                    min_value: 60,
                    min_value_alert: 50,
                  }}
                  title={translate("Rango de presión diastólica")}
                />
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "fc",
                    max_value_alert: 100,
                    max_value: 90,
                    min_value: 60,
                    min_value_alert: 50,
                  }}
                  title={translate("Rango de frecuencia cardíaca")}
                />
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "register_hypertension",
                    max_value_alert: 30,
                    max_value: 7,
                  }}
                  title={translate("Días sin registros de Presión arterial")}
                  onlyMax={true}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

export default BloodPressureCharts;
